/**
 * TEAM-1 — the team invite someone opened before they had an account.
 *
 * /invite/<token> sends a signed-out visitor to sign in or sign up. The
 * `?redirect=` that rides along is lost on the paths that leave the page:
 * Google / Apple sign-in come back to `/`, and so does the "confirm your
 * email" link. Landing on `/` with no business opens setup, which makes
 * the invitee a business of their own before they ever see the invite.
 *
 * So the accept page keeps the token here, and ProtectedRoute sends a
 * signed-in visitor back to it before anything else. The accept page
 * forgets it once the invite is accepted, turned down, or no longer valid.
 */
const KEY = 'orbit:pending-invite';
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // an invite lives 7 days
const TOKEN_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function savePendingInvite(token: string): void {
  if (!TOKEN_RE.test(token)) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ token, at: Date.now() }));
  } catch {
    /* private mode: the ?redirect= path still works */
  }
}

export function loadPendingInvite(): string | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { token?: unknown; at?: unknown };
    if (typeof v.token !== 'string' || !TOKEN_RE.test(v.token)) return null;
    if (typeof v.at !== 'number' || Date.now() - v.at > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return v.token;
  } catch {
    return null;
  }
}

export function clearPendingInvite(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}
